import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  GENDEC_TARGET_TIME_MAX,
  GendecFormats,
  IFlightType,
  IGendecRuleErrors,
  validateGendecRule,
} from "@vmosolutions/oc-interfaces";
import { IGendecRuleRow } from "./RulesGrid";
import { FlightTypeTriStateField } from "./FlightTypeTriStateField";

interface IAddRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (row: IGendecRuleRow) => void;
  flightTypes: IFlightType[];
}

interface IRuleDraft {
  flightNumber: string;
  origin: string;
  destination: string;
  format: "" | GendecFormats;
  sendOrigin: boolean;
  sendDestination: boolean;
  batch: string;
  targetTime: string;
  windowStart: string;
  flightTypeIds: string[];
  excludedFlightTypeIds: string[];
}

const emptyDraft: IRuleDraft = {
  flightNumber: "",
  origin: "",
  destination: "",
  format: "",
  sendOrigin: false,
  sendDestination: false,
  batch: "",
  targetTime: "",
  windowStart: "",
  flightTypeIds: [],
  excludedFlightTypeIds: [],
};

/**
 * Validate a draft via the shared `validateGendecRule` so the dialog can never disagree
 * with the backend's bounds. The draft's string-typed `targetTime`/`windowStart` are
 * passed through as-is - the shared validator handles empty strings and Number coercion.
 */
const validate = (draft: IRuleDraft): IGendecRuleErrors =>
  validateGendecRule({
    flightNumber: draft.flightNumber,
    origin: draft.origin,
    destination: draft.destination,
    format: draft.format,
    sendOrigin: draft.sendOrigin,
    sendDestination: draft.sendDestination,
    batch: draft.batch,
    targetTime: draft.targetTime as unknown as number,
    windowStart: draft.windowStart as unknown as number,
    stopProcessing: false,
    flightTypeIds: draft.flightTypeIds.length > 0 ? draft.flightTypeIds : undefined,
    excludedFlightTypeIds: draft.excludedFlightTypeIds.length > 0 ? draft.excludedFlightTypeIds : undefined,
  });

/**
 * Compose-and-validate dialog for a new GenDec rule. Starts blank (no misleading
 * defaults), validates before the rule is committed, and hands a fully-formed row
 * back to the caller, which appends it to the end of the grid and scrolls to it so
 * it's immediately visible. A blank origin/destination is normalized to the "*" wildcard on commit.
 */
export const AddRuleDialog = (props: IAddRuleDialogProps): JSX.Element => {
  const { open, onClose, onAdd, flightTypes } = props;
  const [draft, setDraft] = useState<IRuleDraft>(emptyDraft);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const errors = useMemo(() => validate(draft), [draft]);
  const hasErrors = Object.keys(errors).length > 0;

  const set = <TKey extends keyof IRuleDraft>(key: TKey, value: IRuleDraft[TKey]): void =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleClose = (): void => {
    setDraft(emptyDraft);
    setShowErrors(false);
    onClose();
  };

  const handleAdd = (): void => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    onAdd({
      _rowId: crypto.randomUUID(),
      flightNumber: draft.flightNumber.trim(),
      origin: draft.origin.trim() || "*",
      destination: draft.destination.trim() || "*",
      format: draft.format as GendecFormats,
      sendOrigin: draft.sendOrigin,
      sendDestination: draft.sendDestination,
      batch: draft.batch.trim(),
      targetTime: Number(draft.targetTime),
      windowStart: Number(draft.windowStart),
      stopProcessing: false,
      // Empty arrays become undefined so the new rule's wire shape matches the dedup
      // signature's "no constraint" form (consistent with the grid's tri-state cell).
      flightTypeIds: draft.flightTypeIds.length > 0 ? draft.flightTypeIds : undefined,
      excludedFlightTypeIds: draft.excludedFlightTypeIds.length > 0 ? draft.excludedFlightTypeIds : undefined,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add GenDec Rule</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Flight #"
              placeholder="blank = any"
              value={draft.flightNumber}
              onChange={(e) => set("flightNumber", e.target.value)}
              error={showErrors && !!errors.flightNumber}
              helperText={showErrors && errors.flightNumber ? errors.flightNumber : "number, range, or /regex/"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Origin"
              placeholder="blank = any"
              value={draft.origin}
              onChange={(e) => set("origin", e.target.value.toUpperCase())}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Destination"
              placeholder="blank = any"
              value={draft.destination}
              onChange={(e) => set("destination", e.target.value.toUpperCase())}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Format"
              value={draft.format}
              onChange={(e) => set("format", e.target.value as GendecFormats)}
              error={showErrors && !!errors.format}
              helperText={showErrors ? errors.format : " "}
            >
              <MenuItem value={GendecFormats.usa}>USA</MenuItem>
              <MenuItem value={GendecFormats.can}>CAN</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Target Hour"
              value={draft.targetTime}
              onChange={(e) => set("targetTime", e.target.value)}
              error={showErrors && !!errors.targetTime}
              helperText={
                showErrors && errors.targetTime
                  ? errors.targetTime
                  : `0-${GENDEC_TARGET_TIME_MAX} (eg. 0 means 00:00 Base Time)`
              }
              inputProps={{ min: 0, max: GENDEC_TARGET_TIME_MAX, step: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Window Start (min)"
              value={draft.windowStart}
              onChange={(e) => set("windowStart", e.target.value)}
              error={showErrors && !!errors.windowStart}
              helperText={
                showErrors && errors.windowStart ? errors.windowStart : "Minutes from target time to start of window"
              }
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Batch"
              placeholder="optional"
              value={draft.batch}
              onChange={(e) => set("batch", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={draft.sendOrigin} onChange={(e) => set("sendOrigin", e.target.checked)} />}
                label="Send Origin"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.sendDestination}
                    onChange={(e) => set("sendDestination", e.target.checked)}
                  />
                }
                label="Send Dest"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Flight Type
            </Typography>
            <FlightTypeTriStateField
              flightTypes={flightTypes}
              includedIds={draft.flightTypeIds}
              excludedIds={draft.excludedFlightTypeIds}
              onChange={(inc, exc) => setDraft((prev) => ({ ...prev, flightTypeIds: inc, excludedFlightTypeIds: exc }))}
              variant="full"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Click an icon to cycle blank -&gt; include -&gt; exclude. Both empty = any flight type.
            </Typography>
          </Grid>

          {showErrors && errors.send && (
            <Grid item xs={12}>
              <Typography color="error" variant="caption">
                {errors.send}
              </Typography>
            </Grid>
          )}
          {showErrors && (errors.flightTypeIds ?? errors.excludedFlightTypeIds) && (
            <Grid item xs={12}>
              <Typography color="error" variant="caption">
                {errors.flightTypeIds ?? errors.excludedFlightTypeIds}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAdd}>
          Add Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRuleDialog;
